'use client';

import { useTranslations } from '@fshr/context/i18n-context';
import { HorizontalLine, Modal } from '../components';
import ImageGalleryComponent from '../image-gallery/image-gallery-component';
import { RoomProps } from '../types/commons';
import { cn } from '@fshr/shared-react';

type FloorPlanElementProps = {
	room: RoomProps;
	closeModal: () => void;
};

const FloorPlanComponent = ({ room, closeModal }: FloorPlanElementProps) => {
	const t = useTranslations();
	const body = (
		<div className="flex flex-col items-center spacing-stack-xl max-w-[930px] pb-400">
			<div className="w-full max-w-full flex lg:flex-col flex-col-reverse lg:flex-row justify-between spacing-grid-gutter spacing-stack-xl lg:spacing-heading-m">
				<div className={cn('w-full flex flex-col flex-1')}>
					<h3 className="text-subtitle1 spacing-heading-s">
						{' '}
						<span className="text-text-heading">{t(room?.name ?? '')}</span>
					</h3>
					<p className="text-body3">
						<span className="text-text-primary">{t(room?.description ?? '')}</span>
					</p>
				</div>
				<div className="lg:w-0 flex-1">
					<ImageGalleryComponent images={room?.images} className="" />
				</div>
			</div>
			<div className="flex flex-col spacing-stack-xl">
				<div className="flex flex-col spacing-stack-m text-subtitle2">
					<span className="text-text-heading">{t('Room Details')}</span>
					<HorizontalLine />

					<div className="flex flex-col spacing-stack-m flex-wrap lg:grid lg:grid-cols-2 lg:grid-col-4 spacing-grid-gutter lg:spacing-stack-s">
						{room?.roomDetails?.map((detail, index) => {
							return detail?.details && detail?.details.length > 0 ? (
								<div key={index} className="flex flex-col  flex-wrap spacing-stack-xs">
									<p className="text-label uppercase">
										<span className="text-text-heading">{t(detail.title)}</span>
									</p>
									{Array.isArray(detail?.details) ? (
										<ul className="text-body3">
											{detail?.details?.map((occupancy, oIndex) => {
												return (
													<li className="text-text-primary" key={oIndex}>
														{t(occupancy)}
													</li>
												);
											})}
										</ul>
									) : (
										<p className="text-body3">
											<span className="text-text-primary">{t(detail?.details ?? '')}</span>
										</p>
									)}
								</div>
							) : null;
						})}
					</div>
				</div>

				<div className="flex flex-col spacing-stack-m text-subtitle2">
					<span className="text-text-heading">{t('Services & Amenities')}</span>
					<HorizontalLine />
					<div className="flex flex-col spacing-stack-m flex-wrap lg:grid lg:grid-cols-2 lg:grid-col-3 spacing-grid-gutter lg:spacing-stack-s">
						{room?.servicesAndAmenities?.map((serviceAmenity, index) => {
							return (
								<div
									className={cn('', {
										'flex flex-col': index % 2 !== 0,
									})}
									key={index}
								>
									<div className="flex flex-col spacing-stack-xs">
										<p className="text-label">
											<span className="text-text-heading">{t(serviceAmenity.title)}</span>
										</p>
										<ul>
											{serviceAmenity.amenities.map((amenity, aIndex) => {
												return (
													<li
														className="flex items-start gap-[10px] text-body3 ml-[10px]"
														key={aIndex}
													>
														<div className="mt-[8px] min-[1024px]:mt-[10px] h-[4px] w-[4px] rounded-full bg-black shrink-0"></div>
														<div className="text-wrap text-text-primary">{t(amenity)}</div>
													</li>
												);
											})}
										</ul>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
	return (
		<Modal
			close={closeModal}
			focusContainerClassNames="max-w-[930px] md:h-auto max-h-[90vh] md:max-h-[95vh]"
			mobileFullScreen={false}
		>
			<div className="flex flex-col flex-1 flex-grow h-full">{body}</div>
		</Modal>
	);
};

export default FloorPlanComponent;
